import React from 'react';
import { makeStyles } from '@mui/styles';
import { Controller } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { maskCpfCnpj, onlyAlphanumeric } from '../utils/documents';
import { COUNTRIES, DEFAULT_COUNTRY_CODE, countryFlag, getCountryByCode } from '../utils/countries';

const useStyles = makeStyles({
    main: {
        lineHeight: '.75rem'
    },
    label: {
        display: "block",
        margin: "0",
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.8,
        color: "#000",
    },
    labelRow: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: "0.5rem"
    },
    labelExtra: {
        fontSize: ".75rem",
        color: "rgba(0,0,0,0.6)"
    },
    input: {
        display: "block",
        WebkitBoxSizing: "border-box",
        boxSizing: "border-box",
        width: "100%",
        height: "2.77083rem",
        margin: "0 0",
        padding: "0.66667rem",
        border: "2px solid #000",
        borderRadius: "8px",
        backgroundColor: "#fff",
        WebkitBoxShadow: "none",
        boxShadow: "none",
        fontFamily: "inherit",
        fontSize: "1rem",
        fontWeight: "normal",
        lineHeight: 1.5,
        color: "#000",
        WebkitTransition: "border-color 0.25s ease-in-out, -webkit-box-shadow 0.5s",
        transition: [
            "border-color 0.25s ease-in-out, -webkit-box-shadow 0.5s",
            "box-shadow 0.5s, border-color 0.25s ease-in-out",
            "box-shadow 0.5s, border-color 0.25s ease-in-out, -webkit-box-shadow 0.5s"
        ],
        WebkitAppearance: "none",
        MozAppearance: "none",
        appearance: "none",
        "&:disabled": {
            backgroundColor: "#e6e6e6",
            cursor: "not-allowed"
        },
        "&:focus": {
            outline: "none",
            border: "2px solid #3CD568",
            backgroundColor: "#fff",
            WebkitBoxShadow: "none",
            boxShadow: "none",
            WebkitTransition: "border-color 0.25s ease-in-out, -webkit-box-shadow 0.5s",
            transition: [
                "border-color 0.25s ease-in-out, -webkit-box-shadow 0.5s",
                "box-shadow 0.5s, border-color 0.25s ease-in-out",
                "box-shadow 0.5s, border-color 0.25s ease-in-out, -webkit-box-shadow 0.5s"
            ]
        }
    },
    // Wrapper used only when the field has a prefix or a suffix: it carries the
    // border/background the bare input gives up, so both render as one control.
    inputWrap: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        WebkitBoxSizing: "border-box",
        boxSizing: "border-box",
        width: "100%",
        height: "2.77083rem",
        padding: "0 0.66667rem",
        border: "2px solid #000",
        borderRadius: "8px",
        backgroundColor: "#fff",
        WebkitTransition: "border-color 0.25s ease-in-out",
        transition: "border-color 0.25s ease-in-out",
        "&:focus-within": {
            border: "2px solid #3CD568"
        }
    },
    adornment: {
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        color: "rgba(0,0,0,0.6)",
        fontSize: ".9375rem",
        lineHeight: 1,
        whiteSpace: "nowrap",
        "& svg": {
            fontSize: "1.25rem"
        }
    },
    bareInput: {
        flex: 1,
        minWidth: 0,
        height: "100%",
        margin: "0",
        padding: "0",
        border: "none",
        backgroundColor: "transparent",
        fontFamily: "inherit",
        fontSize: "1rem",
        fontWeight: "normal",
        lineHeight: 1.5,
        color: "#000",
        WebkitAppearance: "none",
        MozAppearance: "none",
        appearance: "none",
        "&:focus": {
            outline: "none"
        },
        "&:disabled": {
            cursor: "not-allowed"
        }
    },
    // Country selector rendered inside the phone field's prefix slot: a compact
    // "flag + dial code" display with a transparent native <select> on top, so
    // the control keeps the platform's own picker on mobile.
    countrySelect: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "0.25rem",
        flexShrink: 0,
        paddingRight: "0.5rem",
        borderRight: "1px solid rgba(0,0,0,0.15)",
        color: "rgba(0,0,0,0.6)",
        fontSize: ".9375rem",
        lineHeight: 1,
        whiteSpace: "nowrap",
        cursor: "pointer"
    },
    countrySelectFlag: {
        fontSize: "1.125rem",
        lineHeight: 1
    },
    countrySelectCaret: {
        borderTop: "4px solid currentColor",
        borderRight: "4px solid transparent",
        borderLeft: "4px solid transparent",
        opacity: 0.6
    },
    countrySelectInput: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        margin: 0,
        padding: 0,
        border: "none",
        opacity: 0,
        cursor: "pointer",
        WebkitAppearance: "none",
        MozAppearance: "none",
        appearance: "none",
        "&:disabled": {
            cursor: "not-allowed"
        }
    },
    error: {
        lineHeight: '1rem',
        "& label, & p": {
            color: '#cc4b37!important'
        },
        "& input": {
            color: '#cc4b37!important',
            backgroundColor: '#faedeb',
            borderColor: '#cc4b37!important'
        },
        "& $inputWrap": {
            backgroundColor: '#faedeb',
            borderColor: '#cc4b37!important'
        },
        "& $bareInput": {
            backgroundColor: 'transparent'
        },
        "& $errorMessage": {
            color: '#cc4b37!important',
            fontSize: '.75rem'
        },
    },
    errorMessage: {
        display: 'block',
        lineHeight: '1.15rem',
        margin: '5px 0 0 0'
    },
    helper: {
        fontSize: '.75rem',
        lineHeight: '1.15rem',
        margin: '5px 0 0 0'
    }
});

/**
 * Field label. `labelExtra` renders a muted note on the opposite side of the
 * label (e.g. "opcional").
 */
function FieldLabel({ label, labelExtra }) {
    const classes = useStyles();

    if (!labelExtra) {
        return <label className={classes.label}>{label}</label>;
    }

    return (
        <div className={classes.labelRow}>
            <label className={classes.label}>{label}</label>
            <span className={classes.labelExtra}>{labelExtra}</span>
        </div>
    );
}

/**
 * Wraps the control with its prefix/suffix slots (country code, icons, password
 * toggle). Without either slot the control is returned untouched, so fields
 * that do not use them keep rendering exactly as before.
 */
function FieldFrame({ prefix, suffix, children }) {
    const classes = useStyles();

    if (!prefix && !suffix) {
        return children;
    }

    return (
        <div className={classes.inputWrap}>
            {!!prefix && <span className={classes.adornment}>{prefix}</span>}
            {children}
            {!!suffix && <span className={classes.adornment}>{suffix}</span>}
        </div>
    );
}

export function MbTextField({ error, helper, label, labelExtra, prefix, suffix, type, register, inputProps, ...rest }) {
    const classes = useStyles();
    const hasAdornment = !!prefix || !!suffix;

    return (
        <div className={!!error ? classes.error : classes.main}>
            <FieldLabel label={label} labelExtra={labelExtra} />
            <FieldFrame prefix={prefix} suffix={suffix}>
                <input
                    className={hasAdornment ? classes.bareInput : classes.input}
                    style={{ textTransform: type === 'email' ? 'lowercase' : 'none' }}
                    type={type ? type : "text"}
                    {...register}
                    {...inputProps}
                    {...rest}
                />
            </FieldFrame>
            {!!helper && <p className={classes.helper}>{helper}</p>}
            {!!error && <span className={classes.errorMessage}>{error}</span>}
        </div>
    );
}

export function MbMaskedTextField({ error, helper, label, labelExtra, prefix, suffix, register, inputProps, ...rest }) {
    const classes = useStyles();
    const hasAdornment = !!prefix || !!suffix;

    return (
        <div className={!!error ? classes.error : classes.main}>
            <FieldLabel label={label} labelExtra={labelExtra} />
            <FieldFrame prefix={prefix} suffix={suffix}>
                <PatternFormat
                    className={hasAdornment ? classes.bareInput : classes.input}
                    type="text"
                    {...register}
                    {...inputProps}
                    {...rest}
                />
            </FieldFrame>
            {!!helper && <p className={classes.helper}>{helper}</p>}
            {!!error && <span className={classes.errorMessage}>{error}</span>}
        </div>
    );
}

/**
 * Controlled field for a combined CPF/CNPJ input. Unlike `PatternFormat` (which
 * only accepts digits), this accepts the alphanumeric CNPJ format introduced by
 * the Receita Federal on 2026-07-01. The stored value is the raw, unmasked,
 * uppercased alphanumeric string.
 */
export function MbControlledCpfCnpjField({ control, error, helper, label, name, rules, onBlur, ...rest }) {
    const classes = useStyles();

    return (
        <div className={!!error ? classes.error : classes.main}>
            <label className={classes.label}>{label}</label>
            <Controller
                control={control}
                name={name}
                rules={rules}
                render={({ field: { onChange, value } }) => (
                    <input
                        className={classes.input}
                        type="text"
                        autoCapitalize="characters"
                        value={maskCpfCnpj(value)}
                        onBlur={onBlur}
                        onChange={(e) => onChange(onlyAlphanumeric(e.target.value))}
                        {...rest}
                    />
                )}
            />
            {!!error && <span>{error}</span>}
            {!!helper && <p className={classes.helper}>{helper}</p>}
        </div>
    );
}

export function MbControlledMaskedTextField({ control, error, format, helper, label, mask, rules, onBlur, ...rest }) {
    const classes = useStyles();

    return (
        <div className={!!error ? classes.error : classes.main}>
            <label className={classes.label}>{label}</label>
            <Controller
                control={control}
                rules={rules}
                render={({ field: { onChange, value, ref } }) => (
                    <PatternFormat
                        className={classes.input}
                        format={format}
                        mask={mask || '_'}
                        onBlur={onBlur}
                        value={value}
                        onValueChange={(v) => {
                            onChange(v.value)
                        }}
                    />
                )}
                {...rest}
            />
            {!!error && <span>{error}</span>}
            {!!helper && <p className={classes.helper}>{helper}</p>}
        </div>
    );
}
// Brazilian numbers are the common case, so they keep the familiar mask. Other
// countries vary too much in length and grouping for a fixed pattern, so their
// national number is typed as plain digits.
const PHONE_FORMATS = {
    BR: { format: '(##) #####-####', placeholder: '(11) 99999-1234' }
};

const GENERIC_PHONE_PLACEHOLDER = '999999999';
const GENERIC_PHONE_MAX_LENGTH = 15;

function CountrySelect({ value, onChange, disabled }) {
    const classes = useStyles();
    const country = getCountryByCode(value) || getCountryByCode(DEFAULT_COUNTRY_CODE);

    return (
        <span className={classes.countrySelect}>
            <span className={classes.countrySelectFlag}>{countryFlag(country.code)}</span>
            <span>+{country.dial}</span>
            <span className={classes.countrySelectCaret} />
            <select
                className={classes.countrySelectInput}
                aria-label="Código internacional"
                disabled={disabled}
                value={country.code}
                onChange={(e) => onChange(e.target.value)}
            >
                {COUNTRIES.map((option) => (
                    <option key={option.code} value={option.code}>
                        {countryFlag(option.code)} {option.name} (+{option.dial})
                    </option>
                ))}
            </select>
        </span>
    );
}

/**
 * Phone field with an international dialling code selector. The country and the
 * national number are kept as separate values: `countryCode` is an ISO alpha-2
 * code and `value` holds the digits only, so the caller decides how to join
 * them (usually `'+' + dial + value`).
 */
export function MbPhoneField({
    countryCode = DEFAULT_COUNTRY_CODE,
    disabled,
    error,
    helper,
    label,
    labelExtra,
    onCountryCodeChange,
    onValueChange,
    placeholder,
    value
}) {
    const classes = useStyles();
    const phoneFormat = PHONE_FORMATS[countryCode];
    const fieldPlaceholder = placeholder || phoneFormat?.placeholder || GENERIC_PHONE_PLACEHOLDER;

    const prefix = (
        <CountrySelect
            value={countryCode}
            disabled={disabled}
            onChange={(code) => onCountryCodeChange && onCountryCodeChange(code)}
        />
    );

    return (
        <div className={!!error ? classes.error : classes.main}>
            <FieldLabel label={label} labelExtra={labelExtra} />
            <FieldFrame prefix={prefix}>
                {phoneFormat ? (
                    <PatternFormat
                        className={classes.bareInput}
                        type="text"
                        disabled={disabled}
                        format={phoneFormat.format}
                        mask="_"
                        placeholder={fieldPlaceholder}
                        value={value}
                        onValueChange={(values) => onValueChange(values.value)}
                    />
                ) : (
                    <input
                        className={classes.bareInput}
                        type="tel"
                        inputMode="numeric"
                        disabled={disabled}
                        maxLength={GENERIC_PHONE_MAX_LENGTH}
                        placeholder={fieldPlaceholder}
                        value={value}
                        onChange={(e) => onValueChange(e.target.value.replace(/\D/g, ''))}
                    />
                )}
            </FieldFrame>
            {!!helper && <p className={classes.helper}>{helper}</p>}
            {!!error && <span className={classes.errorMessage}>{error}</span>}
        </div>
    );
}

/**
 * `MbPhoneField` bound to react-hook-form. The country and the national number
 * live in two separate form fields (`countryCodeName` and `name`), so the
 * submit joins them with `formatPhoneNumber`.
 */
export function MbControlledPhoneField({ control, countryCodeName, name, ...rest }) {
    return (
        <Controller
            control={control}
            name={countryCodeName}
            render={({ field: countryField }) => (
                <Controller
                    control={control}
                    name={name}
                    render={({ field }) => (
                        <MbPhoneField
                            {...rest}
                            countryCode={countryField.value}
                            // Changing the country clears the number, since its
                            // length and mask no longer apply.
                            onCountryCodeChange={(code) => {
                                countryField.onChange(code);
                                field.onChange('');
                            }}
                            value={field.value}
                            onValueChange={field.onChange}
                        />
                    )}
                />
            )}
        />
    );
}
